import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  Button,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { getRecentAMIs, getAMIById, getCounts } from '../services/api';
import type { ProcessedAMI } from '../types/api';
import Navbar from '../components/Navbar';

// Interface for table row data that combines ProcessedAMI with optional AMI details
interface TableRowData {
  amiId: string;
  name: string;
  ownerId: string;
  imageOwnerAlias?: string;
  regionFound: string;
  processedAt: string; // Changed from creationDate to processedAt
  totalAmisForOwner: number;
}

const Home = () => {
  const navigate = useNavigate();

  // Recent AMIs state
  const [recentAMIs, setRecentAMIs] = useState<TableRowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const AMIS_PER_PAGE = 10;
  const MAX_PAGES = 10;

  // Counts state
  const [counts, setCounts] = useState<{ amiCount: number; hashCount: number; accountCount: number; scannedAmisCount: number }>({ amiCount: 0, hashCount: 0, accountCount: 0, scannedAmisCount: 0 });
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState<string | null>(null);

  // Search state
  const [activeTab, setActiveTab] = useState(0);
  const [imageSearchTerm, setImageSearchTerm] = useState('');
  const [imageSearchType, setImageSearchType] = useState<'ami' | 'account' | 'name'>('name');
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  const [fileSearchMode, setFileSearchMode] = useState<'hash' | 'file'>('file');
  const [fileSearchType, setFileSearchType] = useState<'filename' | 'filepath'>('filename');
  const [fileMatchType, setFileMatchType] = useState<'exact' | 'contains'>('contains');

  // Fetch counts data
  useEffect(() => {
    setCountsLoading(true);
    setCountsError(null);
    getCounts()
      .then((res) => {
        if (res.error) {
          setCountsError(res.error);
        } else {
          setCounts(res.data);
        }
        setCountsLoading(false);
      })
      .catch(() => {
        setCountsError('Failed to fetch counts data');
        setCountsLoading(false);
      });
  }, []);

  // Fetch recent AMIs data
  useEffect(() => {
    setLoading(true);
    setError(null);
    getRecentAMIs()
      .then(async (res) => {
        if (res.error) {
          setError(res.error);
          setLoading(false);
          return;
        }
        
        // Static list of verified accounts to exclude
        const VERIFIED_ACCOUNTS = ['137112412989', '099720109477', '533267102885'];
        
        // Filter to only show AMIs with completed status, exclude Amazon-owned AMIs and verified accounts, and only show accounts with < 25 AMIs
        const filteredData = res.data.filter(item => 
          item.status === 'completed' && 
          item.accountId !== 'amazon' &&
          !VERIFIED_ACCOUNTS.includes(item.accountId) &&
          (item.totalAmisForOwner === undefined || item.totalAmisForOwner < 25)
        );
        const processed = filteredData.slice(0, AMIS_PER_PAGE * MAX_PAGES);
        
        // Separate AMIs that have complete data vs those that need additional API calls
        const completeAMIs: TableRowData[] = [];
        const incompleteAMIs: ProcessedAMI[] = [];
        
        processed.forEach((item) => {
          if (item.amiName && item.accountId && item.accountId !== 'unknown') {
            // We have all the data we need from the processed-amis endpoint
            completeAMIs.push({
              amiId: item.amiId,
              name: item.amiName,
              ownerId: item.accountId,
              regionFound: item.region,
              processedAt: item.processedAt,
              totalAmisForOwner: item.totalAmisForOwner,
            });
          } else {
            // We need to fetch additional data
            incompleteAMIs.push(item);
          }
        });
        
        // Fetch details for incomplete AMIs
        const additionalDetails = await Promise.all(
          incompleteAMIs.map(async (item) => {
            try {
              const detailRes = await getAMIById(item.amiId);
              if (detailRes.data && Array.isArray(detailRes.data) && detailRes.data.length > 0) {
                const amiData = detailRes.data[0];
                return {
                  amiId: item.amiId,
                  name: amiData.name || 'Unknown',
                  ownerId: amiData.ownerId || item.accountId || 'Unknown',
                  imageOwnerAlias: amiData.imageOwnerAlias,
                  regionFound: amiData.regionFound || item.region || 'Unknown',
                  processedAt: item.processedAt,
                  totalAmisForOwner: item.totalAmisForOwner,
                } as TableRowData;
              } else if (detailRes.data && detailRes.data.amiId) {
                const amiData = detailRes.data;
                return {
                  amiId: item.amiId,
                  name: amiData.name || 'Unknown',
                  ownerId: amiData.ownerId || item.accountId || 'Unknown',
                  imageOwnerAlias: amiData.imageOwnerAlias,
                  regionFound: amiData.regionFound || item.region || 'Unknown',
                  processedAt: item.processedAt,
                  totalAmisForOwner: item.totalAmisForOwner,
                } as TableRowData;
              } else {
                // Fallback to processed AMI data
                return {
                  amiId: item.amiId,
                  name: 'Unknown',
                  ownerId: item.accountId || 'Unknown',
                  regionFound: item.region || 'Unknown',
                  processedAt: item.processedAt,
                  totalAmisForOwner: item.totalAmisForOwner,
                } as TableRowData;
              }
            } catch {
              // Fallback to processed AMI data
              return {
                amiId: item.amiId,
                name: 'Unknown',
                ownerId: item.accountId || 'Unknown',
                regionFound: item.region || 'Unknown',
                processedAt: item.processedAt,
                totalAmisForOwner: item.totalAmisForOwner,
              } as TableRowData;
            }
          })
        );
        
        // Combine complete and fetched data
        let allAMIs = [...completeAMIs, ...additionalDetails.filter(Boolean)];
        // Exclude any with imageOwnerAlias 'amazon' (case-insensitive)
        allAMIs = allAMIs.filter(ami => !ami.imageOwnerAlias || ami.imageOwnerAlias.toLowerCase() !== 'amazon');
        setRecentAMIs(allAMIs);
        setLoading(false);
      })
      .catch(() => setError('Failed to fetch recent AMIs'));
  }, []);

  const handleImageSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageSearchTerm.trim()) return;
    
    // Handle direct navigation for AMI ID and Account ID
    if (imageSearchType === 'ami') {
      navigate(`/image/${imageSearchTerm.trim()}`);
      return;
    }
    if (imageSearchType === 'account') {
      navigate(`/account/${imageSearchTerm.trim()}`);
      return;
    }
    
    // Handle name search - redirect to search page
    navigate(`/search?name=${encodeURIComponent(imageSearchTerm.trim())}`);
  };

  const handleFileSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileSearchTerm.trim()) return;
    
    // Handle hash lookup directly
    if (fileSearchMode === 'hash') {
      navigate(`/hash/${fileSearchTerm.trim()}`);
      return;
    }
    
    // Handle file search - redirect to search page with parameters
    const params = new URLSearchParams();
    params.set('searchType', fileSearchType);
    params.set('matchType', fileMatchType);
    params.set('searchTerm', fileSearchTerm.trim());
    params.set('limit', '100');
    
    navigate(`/search?${params.toString()}`);
  };


  const getSearchPlaceholder = () => {
    switch (imageSearchType) {
      case 'ami': return 'e.g., ami-12345678';
      case 'account': return 'e.g., 123456789012';
      case 'name': return 'e.g., ubuntu-20.04, centos*-stream, amzn2-*';
      default: return '';
    }
  };

  const getFileSearchPlaceholder = () => {
    if (fileSearchMode === 'hash') {
      return 'e.g., d41d8cd98f00b204e9800998ecf8427e';
    }
    return fileSearchType === 'filename' ? 'e.g., bash, config.json' : 'e.g., /usr/bin/bash, /etc/config.json';
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 1 }}>
        <Navbar />
      </Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, mt: 2, fontSize: '1.125rem', fontWeight: 'bold' }}>
          Search and explore public cloud images, their lineage, and their filesystems
        </Typography>

        <Alert severity="warning" sx={{ maxWidth: 750, mx: 'auto', mb: 2, fontSize: '0.875rem', background: '#fffbe6', color: '#8a6d1b', border: '1px solid #ffe58f', py: 1 }}>
          <div style={{ textAlign: 'center' }}>
            <strong>This site is in public preview.</strong>{' '}
            <Link to="/faq" style={{ color: '#8a6d1b', textDecoration: 'underline' }}>
              Click here to see the known issues and current limitations.
            </Link>
          </div>
        </Alert>
      </Box>
      

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Search Images" />
          <Tab label="Search Files" />
        </Tabs>

        {/* Image Search Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            <form onSubmit={handleImageSearch}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Search by</FormLabel>
                <RadioGroup
                  row
                  value={imageSearchType}
                  onChange={(e) => setImageSearchType(e.target.value as 'ami' | 'account' | 'name')}
                >                  
                  <FormControlLabel value="name" control={<Radio size="small" />} label="AMI Name" sx={{ mr: 1 }} />
                  <FormControlLabel value="account" control={<Radio size="small" />} label="Account ID" sx={{ mr: 1 }} />
                  <FormControlLabel value="ami" control={<Radio size="small" />} label="AMI ID" sx={{ mr: 0 }} />
                </RadioGroup>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Search term"
                  variant="outlined"
                  size="small"
                  value={imageSearchTerm}
                  onChange={e => setImageSearchTerm(e.target.value)}
                  placeholder={getSearchPlaceholder()}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  Search
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {imageSearchType === 'ami' && 'Enter an AMI ID to view details'}
                {imageSearchType === 'account' && 'Enter an AWS Account ID to view all AMIs'}
                {imageSearchType === 'name' && 'Search by AMI name with regex support (e.g., ubuntu*-server, amzn2-*-2023)'}
              </Typography>
            </form>
          </Box>
        )}

        {/* File Search Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <form onSubmit={handleFileSearch}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Search mode</FormLabel>
                <RadioGroup
                  row
                  value={fileSearchMode}
                  onChange={(e) => setFileSearchMode(e.target.value as 'hash' | 'file')}
                >
                  <FormControlLabel value="file" control={<Radio size="small" />} label="File Name Lookup" sx={{ mr: 1 }} />
                  <FormControlLabel value="hash" control={<Radio size="small" />} label="Hash ID Lookup (MD5)" sx={{ mr: 0 }} />
                </RadioGroup>
              </Box>

              {fileSearchMode === 'file' && (
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Search in:</FormLabel>
                    <RadioGroup
                      row
                      value={fileSearchType}
                      onChange={(e) => setFileSearchType(e.target.value as 'filename' | 'filepath')}
                      sx={{ ml: 0.5 }}
                    >
                      <FormControlLabel 
                        value="filename" 
                        control={<Radio size="small" />} 
                        label="Filename"
                        sx={{ mr: 1 }}
                      />
                      <FormControlLabel 
                        value="filepath" 
                        control={<Radio size="small" />} 
                        label="Full path"
                        sx={{ mr: 0 }}
                      />
                    </RadioGroup>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Match:</FormLabel>
                    <RadioGroup
                      row
                      value={fileMatchType}
                      onChange={(e) => setFileMatchType(e.target.value as 'exact' | 'contains')}
                      sx={{ ml: 0.5 }}
                    >
                      <FormControlLabel 
                        value="exact" 
                        control={<Radio size="small" />} 
                        label="Exact"
                        sx={{ mr: 1 }}
                      />
                      <FormControlLabel 
                        value="contains" 
                        control={<Radio size="small" />} 
                        label="Contains"
                        sx={{ mr: 0 }}
                      />
                    </RadioGroup>
                  </Box>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label={fileSearchMode === 'hash' ? 'Hash ID' : 'Search term'}
                  variant="outlined"
                  size="small"
                  value={fileSearchTerm}
                  onChange={e => setFileSearchTerm(e.target.value)}
                  placeholder={getFileSearchPlaceholder()}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  {fileSearchMode === 'hash' ? 'Lookup' : 'Search'}
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {fileSearchMode === 'hash' && 'Enter a file hash to see which AMIs contain it'}
                {fileSearchMode === 'file' && 'Search for files across all scanned AMIs'}
              </Typography>
            </form>
          </Box>
        )}
      </Paper>


      {/* Recent AMIs Table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Most recently scanned public images from accounts with fewer than 25 public AMIs
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Image ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell align="center">Owner AMIs</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Scan Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentAMIs
                    .slice((page - 1) * AMIS_PER_PAGE, page * AMIS_PER_PAGE)
                    .map((ami) => (
                      <TableRow key={ami.amiId} hover>
                        <TableCell>
                          <Link to={`/image/${ami.amiId}`}>{ami.amiId}</Link>
                        </TableCell>
                        <TableCell>{ami.name || 'Unknown'}</TableCell>
                        <TableCell>
                          {ami.ownerId && !ami.imageOwnerAlias ? (
                            <Link to={`/account/${ami.ownerId}`}>{ami.ownerId}</Link>
                          ) : (
                            ami.imageOwnerAlias || ami.ownerId || 'Unknown'
                          )}
                        </TableCell>
                        <TableCell align="center">{ami.totalAmisForOwner}</TableCell>
                        <TableCell>{ami.regionFound || 'Unknown'}</TableCell>
                        <TableCell>{ami.processedAt ? new Date(ami.processedAt).toLocaleString(undefined, {
                          timeZone: 'UTC',
                          timeZoneName: 'short'
                        }) : 'Unknown'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2} mb={4}>
              <Pagination
                count={Math.min(MAX_PAGES, Math.ceil(recentAMIs.length / AMIS_PER_PAGE))}
                page={page}
                onChange={(_e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>

      {/* Database Summary Stats */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Database Summary
        </Typography>
        {countsLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
            <CircularProgress />
          </Box>
        ) : countsError ? (
          <Alert severity="error">{countsError}</Alert>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {counts.amiCount.toLocaleString()}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  AMIs in Database
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {(counts.scannedAmisCount || 0).toLocaleString()}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  AMIs Scanned
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {counts.hashCount.toLocaleString()}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  Unique File Hashes
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', minWidth: '150px' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {counts.accountCount.toLocaleString()}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  AWS Accounts
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Home; 